const OPENCV_SCRIPT =
  "https://cdn.jsdelivr.net/npm/@techstark/opencv-js@4.10.0-release.1/dist/opencv.js";
const LOAD_TIMEOUT_MS = 30000;

let cvPromise = null;

function waitForCvRuntime(candidate) {
  if (candidate instanceof Promise) {
    return candidate.then((cv) =>
      cv?.Mat ? { cv } : waitForCvRuntime(cv)
    );
  }

  const cv = candidate;

  if (!cv) throw new Error("OpenCV loaded without exposing its runtime");
  // Never resolve a Promise with `cv` directly. This distribution keeps a
  // self-referential `.then()` method even after initialization, so Promise
  // resolution would recurse forever and freeze the renderer.
  if (cv.Mat) return Promise.resolve({ cv });

  return new Promise((resolve, reject) => {
    const previous = cv.onRuntimeInitialized;
    let poll;
    const timeout = window.setTimeout(() => {
      window.clearInterval(poll);
      reject(new Error("OpenCV took too long to initialize"));
    }, LOAD_TIMEOUT_MS);
    const finish = () => {
      if (!cv.Mat) return;
      window.clearTimeout(timeout);
      window.clearInterval(poll);
      resolve({ cv });
    };

    cv.onRuntimeInitialized = () => {
      try {
        if (typeof previous === "function") previous();
      } finally {
        finish();
      }
    };

    // Some OpenCV builds finish between the script load event and the
    // callback assignment. Polling closes that race without reloading WASM.
    poll = window.setInterval(finish, 25);
  });
}

function appendScript() {
  return new Promise((resolve, reject) => {
    let script = document.querySelector('script[data-opencv="true"]');

    const loaded = () => {
      waitForCvRuntime(window.cv).then(resolve, reject);
    };
    const failed = () => reject(new Error("Failed to load OpenCV.js"));

    if (script) {
      if (window.cv) {
        loaded();
      } else {
        script.addEventListener("load", loaded, { once: true });
        script.addEventListener("error", failed, { once: true });
      }
      return;
    }

    script = document.createElement("script");
    script.src = OPENCV_SCRIPT;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.dataset.opencv = "true";
    script.addEventListener("load", loaded, { once: true });
    script.addEventListener("error", failed, { once: true });
    document.head.appendChild(script);
  });
}

export function loadOpenCV() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("OpenCV is only available in the browser"));
  }

  if (!cvPromise) {
    cvPromise = (window.cv
      ? waitForCvRuntime(window.cv)
      : appendScript()
    ).catch((error) => {
      // A network failure should not permanently poison future retries.
      cvPromise = null;
      throw error;
    });
  }

  return cvPromise;
}
